"use client";

import { useCallback, useState } from "react";

import { APP_URL } from "@/config/urls";

/**
 * Atom Paynetz checkout, ported from the source project's `useAtomPayment`.
 *
 * The SDK is a third-party script that must be on the page before its global
 * constructor exists, so it is injected on demand — the internship widget is the
 * only thing that needs it, and most visitors never reach the payment step.
 *
 * Flow: the enrolment call returns a one-time token, the SDK is loaded, and
 * `new AtomPaynetz(...)` opens a full-screen iframe. Atom then redirects the
 * browser back to the return URL with a `transactionId` query parameter.
 */

declare global {
  interface Window {
    /** Injected by the Atom checkout script; constructed to open the iframe. */
    AtomPaynetz?: new (options: Record<string, unknown>, mode: string) => void;
  }
}

export interface AtomPaymentGrant {
  token: string;
  merchantID: string;
  returnUrl: string;
  redirectionUrl?: string;
}

const STYLE_ELEMENT_ID = "atom-payment-styles";

/**
 * Atom environment the SDK is constructed with. Hard-coded to match the source
 * project, which passes "uat" literally and has no variable for it. Change it
 * here when moving to production.
 */
const ATOM_MODE = "uat";

/**
 * The SDK host comes from the environment with NO built-in default — see
 * `config/urls`. Returns null (having logged why) when it is missing, so a
 * misconfigured deployment refuses to open checkout rather than quietly loading
 * whichever host a hard-coded literal named.
 */
const resolveAtomSdkUrl = (): string | null => {
  const sdkUrl = APP_URL.ATOM_SDK_URL?.trim();
  if (sdkUrl) return sdkUrl;

  console.error(
    "[useAtomPayment] NEXT_PUBLIC_ATOM_SDK_URL not set — payment cannot start."
  );
  return null;
};

/** Detects an already-injected SDK by its configured URL, not a fixed filename. */
const findExistingScript = (sdkUrl: string) =>
  document.querySelector<HTMLScriptElement>(
    `script[src="${CSS.escape(sdkUrl)}"]`
  );

/**
 * The SDK renders its iframe inline by default, which lands it inside the
 * dialog's scroll container. This pins it to the viewport instead.
 */
const injectAtomStyles = () => {
  if (document.querySelector(`#${STYLE_ELEMENT_ID}`)) return;

  const style = document.createElement("style");
  style.id = STYLE_ELEMENT_ID;
  style.textContent = `
      .atomIframe {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        z-index: 9999 !important;
        border: none !important;
      }
    `;
  document.head.appendChild(style);
};

const SCRIPT_ERROR = "Failed to load Atom Paynetz script";
/** Shown to the student; the console carries which variable is missing. */
const CONFIG_ERROR = "Payment is not configured. Please contact support.";

export function useAtomPayment() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openAtomPayment = useCallback(
    (grant: AtomPaymentGrant, email: string, mobile: string) => {
      try {
        new window.AtomPaynetz!(
          {
            atomTokenId: grant.token,
            merchId: grant.merchantID,
            custEmail: email,
            custMobile: mobile,
            returnUrl: grant.returnUrl,
            userAgent: "mobile_webView",
          },
          ATOM_MODE
        );
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to initialize payment"
        );
        console.error("Atom payment initialization error:", err);
      }
    },
    []
  );

  const initiateAtomPayment = useCallback(
    (grant: AtomPaymentGrant, email: string, mobile: string) => {
      const sdkUrl = resolveAtomSdkUrl();
      if (!sdkUrl) {
        setError(CONFIG_ERROR);
        return;
      }

      setIsLoading(true);
      setError(null);
      injectAtomStyles();

      // Already loaded (e.g. the student came back and retried) — open directly.
      if (typeof window.AtomPaynetz !== "undefined") {
        openAtomPayment(grant, email, mobile);
        setIsLoading(false);
        return;
      }

      const existing = findExistingScript(sdkUrl);

      // In the DOM but still downloading — wait rather than injecting a second copy.
      if (existing) {
        existing.addEventListener(
          "load",
          () => {
            openAtomPayment(grant, email, mobile);
            setIsLoading(false);
          },
          { once: true }
        );
        existing.addEventListener(
          "error",
          () => {
            setError(SCRIPT_ERROR);
            setIsLoading(false);
          },
          { once: true }
        );
        return;
      }

      const script = document.createElement("script");
      script.src = sdkUrl;
      script.onload = () => {
        openAtomPayment(grant, email, mobile);
        setIsLoading(false);
      };
      script.onerror = () => {
        setError(SCRIPT_ERROR);
        setIsLoading(false);
        console.error(SCRIPT_ERROR);
      };
      document.body.appendChild(script);
    },
    [openAtomPayment]
  );

  return { initiateAtomPayment, isLoading, error };
}
